import { Injectable } from '@angular/core'
import { ApiService } from '@app/core/graphql/services/api.service'
import { AuthService } from '@app/core/auth/services/auth.service'
import { ClientRole, ClientRoleMember, CreateClientRoleInput, UpdateClientRoleInput } from '@app/models/client_role.model'
import { ServicesSubscriberEvent } from '@app/models/others.model'
import { GET_CLIENT_ROLES, GET_CLIENT_ROLES_WITH_EMPLOYEES, GET_PASSES_BY_CLIENT_ROLE, SET_CLIENT_ROLE, UPDATE_CLIENT_ROLE } from '@app/query/client_role.query'
import { TranslateMessagesService } from '@app/shared/services/translate-messages.service'
import { serializeAs } from '@app/utils/serialize'
import { pick } from 'lodash-es'
import { MessageService } from 'primeng/api'
import { Observable, Subject, catchError, map, of, shareReplay, tap } from 'rxjs'

@Injectable({
  providedIn: 'root',
})
export class ClientRoleService {
  subscriber: Subject<ServicesSubscriberEvent> = new Subject<ServicesSubscriberEvent>()
  id: string = 'ROLES'
  sysMessage: any

  /** Cache of get_client_roles keyed by category + withEmployees flavor (the two flavors return
   *  different shapes, so a lean cached list must never be handed to a caller that renders
   *  member counts). Roles change through this service's own createNew/update, and as a side
   *  effect of creating the first apartment (PAPI seeds owner/tenant/guest — ApartmentService
   *  clears the cache then); each clears the cache, so within a session each list is fetched once
   *  per key instead of on every screen that reads it (dashboards, pass-modal, settings). */
  private rolesCache = new Map<string, Observable<ClientRole[]>>()

  /** The session identity (getIdentityKey — cid:uid claims, NOT the raw JWT, which PAPI re-signs
   *  on every response) the cached lists were fetched under. get_client_roles is client- AND
   *  user-scoped — PAPI resolves the client from the token and filters by the user's assigned
   *  role ids (crids) — and logout/login are SPA navigations that keep this root service alive,
   *  so without this a user who disconnects from one client and connects to another would be
   *  served the previous client's roles from cache. */
  private rolesCacheIdentity: string | null = null

  constructor(
    private API: ApiService,
    private translateMessagesService: TranslateMessagesService,
    private messageService: MessageService,
    private authService: AuthService,
  ) {
    this.sysMessage = this.translateMessagesService.create(this.id)
  }

  /**
   * Create new client role
   * @param payload
   * @returns
   */
  createNew(payload: Partial<CreateClientRoleInput>) {
    return this.API.privateMutation<any>(SET_CLIENT_ROLE, (data: any) => {
      return {
        data: serializeAs(CreateClientRoleInput, pick(Object.assign(payload, data), Object.keys(new CreateClientRoleInput()))) as CreateClientRoleInput,
      }
    }).pipe(
      tap((data: any) => {
        if (data.success) {
          this.invalidateRolesCache()
          this.messageService.add({
            severity: 'success',
            icon: 'pi pi-check-circle color-success',
            ...this.sysMessage.message('CREATE_SUCCESS', data.data as CreateClientRoleInput),
          })
          this.subscriber.next({
            type: `SET_${this.id}`,
            data: data.data as CreateClientRoleInput,
          })
        }
      })
    )
  }

  /**
   * Update client role
   * @param payload
   * @returns
   */
  update(payload: UpdateClientRoleInput) {
    return this.API.privateMutation<any>(UPDATE_CLIENT_ROLE, (data: any) => {
      const obj = pick(Object.assign(payload, data), Object.keys(new UpdateClientRoleInput()))

      const returnObj = serializeAs(UpdateClientRoleInput, obj) as UpdateClientRoleInput

      return {
        data: returnObj,
      }
    }).pipe(
      tap((data: any) => {
        if (data.success) {
          this.invalidateRolesCache()
          this.messageService.add({
            severity: 'success',
            icon: 'pi pi-check-circle color-success',
            ...this.sysMessage.message('UPDATE_SUCCESS', data.data as UpdateClientRoleInput),
          })
          this.subscriber.next({
            type: `UPDATE_${this.id}`,
            data: data.data as UpdateClientRoleInput,
          })
        }
      })
    )
  }

  /**
   * Get client role
   * @param options.withEmployees also resolve the per-role pass members (expensive on PAPI —
   * request it only when the screen renders member counts/lists, YB-1985)
   * @returns Client roles list
   */
  getClientRoles(category: 'employees' | 'authorized' | 'vehicles' | 'hme' | null, options?: { withEmployees?: boolean }) {
    this.dropCacheOnIdentityChange()

    const withEmployees = options?.withEmployees ?? false
    const cacheKey = `${category ?? '__all__'}${withEmployees ? ':withEmployees' : ''}`
    const cached = this.rolesCache.get(cacheKey)
    if (cached) return cached

    const response = this.API.privateQuery<any>(withEmployees ? GET_CLIENT_ROLES_WITH_EMPLOYEES : GET_CLIENT_ROLES, () => {
      const params: any = {}
      if (category !== null) {
        params.category = category
      }
      return params
    }).pipe(
      map((response: any) => {
        if (response.success) {
          return response.data as ClientRole[]
        } else {
          return [] as ClientRole[]
        }
      }),
      tap((client_roles: ClientRole[] | []) => {
        this.subscriber.next({
          type: `GET_${this.id}S`,
          data: client_roles,
        })
      }),
      // Don't cache a failed fetch — drop the entry so the next caller retries the server.
      catchError(() => {
        this.rolesCache.delete(cacheKey)
        return of([] as ClientRole[])
      }),
      shareReplay(1),
    )

    this.rolesCache.set(cacheKey, response)
    return response
  }

  /**
   * One page of the passes assigned to a role — the role drawer's member list (YB-2073).
   *
   * Not cached, deliberately: it is keyed by search text and page, and it is read while an admin
   * is typing. The role LIST is cached because it is re-read by every screen; a member page is
   * read by one open drawer.
   */
  getPassesByClientRole(clientRoleId: number, params?: { search?: string; skip?: number; take?: number }) {
    return this.API.privateQuery<any>(GET_PASSES_BY_CLIENT_ROLE, () => ({
      client_role_id: Number(clientRoleId),
      search: params?.search || null,
      skip: params?.skip ?? 0,
      take: params?.take ?? 25,
    })).pipe(
      map((response: any) => ({
        data: (response?.success && Array.isArray(response.data) ? response.data : []) as ClientRoleMember[],
        total: response?.success ? Number(response.total) || 0 : 0,
      })),
      catchError(() => of({ data: [] as ClientRoleMember[], total: 0 }))
    )
  }

  /** Invalidate the cached role lists after any create/update so readers get the fresh data. Public
   *  for writes elsewhere that create roles on the server as a side effect (YB-2294: the first
   *  apartment seeds the residential roles). */
  invalidateRolesCache() {
    this.rolesCache.clear()
  }

  /** Drop every cached list when the session identity is no longer the one it was fetched under
   *  (see rolesCacheIdentity). Stateless by design: comparing at the point of use needs no
   *  auth-event subscription, so it can't miss a login path that sets the token without emitting. */
  private dropCacheOnIdentityChange() {
    const identity = this.authService.getIdentityKey()
    if (identity !== this.rolesCacheIdentity) {
      this.rolesCache.clear()
      this.rolesCacheIdentity = identity
    }
  }
}
